/**
 * 무인증 토큰 화면용 WebSocket 훅 — 네임스페이스 `/ws/public`
 * (결정 2026-09-10-public-token-websocket-room).
 *
 * 인증 사용자 소켓(`stores/websocket.ts`·`lib/websocket.ts`)과 **완전히 별개**다 — 그쪽은
 * accessToken·조직 방·토큰 갱신 루프를 가졌고, 이 화면은 그 어느 것도 없다(인증 store·API
 * import 금지 규약). 여기서 하는 일은 연결·재연결·해제와 토큰 방 이벤트 2종의 배선뿐이다.
 *
 * - handshake `auth: { kind, token }` → 서버가 검증기로 방(`public:<kind>:<rowId>`)에 넣는다.
 * - 수신 전용 — 이 소켓으로 보내는 것은 없다(§6). 쓰기는 REST `@Public`이 전부다.
 * - 서버 거부(`error` + disconnect)는 재시도하지 않는다 — 토큰이 무효면 REST도 같은 판정을
 *   내므로 화면은 그쪽에서 만료로 접힌다.
 * - 재연결(`reconnect`)을 호출부에 알린다 — 끊긴 사이의 사건을 놓치지 않게 형제 목록을 한 번
 *   다시 받는 것은 호출부 몫이다(P7).
 *
 * 콜백은 ref로 들고 있어 핸들러가 바뀌어도 소켓을 다시 만들지 않는다. 소켓 생성·해제 축은
 * `kind`·`token`·`enabled`뿐이다.
 */
import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type {
  PublicBundleChangedPayload,
  PublicTokenKind,
  PublicTokenRevokedPayload,
} from '@safetyhub/shared';
import { ServerEvents } from '../types/websocket';

// 인증 소켓(`lib/websocket.ts`)과 같은 값 — 그 모듈은 인증 API를 끌고 오므로 import하지 않는다.
const WS_URL = import.meta.env.VITE_WS_URL || 'http://localhost:3000';
const PUBLIC_WS_NAMESPACE = '/ws/public';

export interface UsePublicTokenSocketOptions {
  kind: PublicTokenKind;
  /** 없으면 연결하지 않는다(라우트 파라미터 미해소 등). */
  token: string | undefined;
  /** false면 연결하지 않는다(만료·완료 화면처럼 더 들을 것이 없을 때). 기본 true. */
  enabled?: boolean;
  onBundleChanged?: (payload: PublicBundleChangedPayload) => void;
  onTokenRevoked?: (payload: PublicTokenRevokedPayload) => void;
  /** 끊겼다가 다시 붙었을 때(첫 연결에는 부르지 않는다). */
  onReconnect?: () => void;
}

export interface UsePublicTokenSocketResult {
  isConnected: boolean;
}

export function usePublicTokenSocket({
  kind,
  token,
  enabled = true,
  onBundleChanged,
  onTokenRevoked,
  onReconnect,
}: UsePublicTokenSocketOptions): UsePublicTokenSocketResult {
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef({ onBundleChanged, onTokenRevoked, onReconnect });
  handlersRef.current = { onBundleChanged, onTokenRevoked, onReconnect };

  useEffect(() => {
    if (!token || !enabled) {
      setIsConnected(false);
      return;
    }

    const socket: Socket = io(`${WS_URL}${PUBLIC_WS_NAMESPACE}`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { kind, token },
      /**
       * 인증 소켓(`lib/websocket.ts`, `/ws`)과 **Manager를 공유하지 않는다**(2026-09-14 리뷰 C3).
       *
       * socket.io-client는 같은 origin+path면 네임스페이스가 달라도 Manager 하나를 캐시해
       * 다중화한다. 그러면 이 훅의 cleanup이 Manager 리스너를 건드릴 때 인증 소켓이 그
       * Manager에 건 `reconnect_attempt`(JWT 갱신)까지 함께 사라져, 토큰 만료 뒤 인증 WS가
       * 옛 토큰으로 재접속을 시도한다. 이 화면은 인증 소켓과 무관하므로 전용 Manager를 쓴다.
       */
      forceNew: true,
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 20,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      timeout: 10000,
    });

    socket.on('connect', () => setIsConnected(true));
    socket.on('disconnect', () => setIsConnected(false));
    const handleReconnect = () => {
      handlersRef.current.onReconnect?.();
    };
    socket.io.on('reconnect', handleReconnect);
    socket.on(ServerEvents.PUBLIC_BUNDLE_CHANGED, (payload: PublicBundleChangedPayload) => {
      handlersRef.current.onBundleChanged?.(payload);
    });
    socket.on(ServerEvents.PUBLIC_TOKEN_REVOKED, (payload: PublicTokenRevokedPayload) => {
      handlersRef.current.onTokenRevoked?.(payload);
    });

    socket.connect();

    return () => {
      // 소켓(네임스페이스) 리스너는 이 훅의 것뿐이라 통째로 걷어도 된다.
      socket.removeAllListeners();
      // Manager 리스너는 **내가 건 것만** 푼다 — `socket.io.removeAllListeners()`는 Manager를
      // 공유하는 다른 소켓의 리스너까지 지운다(위 forceNew 주석). forceNew로 이미 전용
      // Manager지만, 옵션이 되돌려져도 남을 피해가 없도록 해제 방식도 개별로 둔다.
      socket.io.off('reconnect', handleReconnect);
      socket.disconnect();
      setIsConnected(false);
    };
  }, [kind, token, enabled]);

  return { isConnected };
}
